/**
 * Backend-sourced connection status for onboarding + settings.
 * Source of truth: GET /api/connections (PostgreSQL `connections` table).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAppStore } from "@/store/useAppStore";
import { connectionsApi } from "@/lib/api";
import { rowByKind } from "@/lib/connectionRows";
import { isConfiguredStatus } from "@/lib/connectionStatus";

import { ONBOARDING_CHANNEL_KINDS, CONNECTION_LABELS as REGISTRY_LABELS } from "@/lib/channelRegistry";

export { isConfiguredStatus } from "@/lib/connectionStatus";

/** Kinds surfaced in the onboarding wizard (calendar shares Gmail OAuth). */
export const ONBOARDING_CONNECTION_KINDS = ONBOARDING_CHANNEL_KINDS;

export const CONNECTION_LABELS = {
  ...REGISTRY_LABELS,
  calendar: "Google Calendar",
};

export function useBackendConnections({ autoSeed = true } = {}) {
  const { auth, isAuthed } = useAppStore();
  const role = auth?.server?.role || "owner";
  const canEdit = role === "owner" || role === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const r = await connectionsApi.list();
    const list = Array.isArray(r?.connections) ? r.connections : [];
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug("[connections] loaded", {
        count: list.length,
        kinds: list.map((row) => row?.kind).filter(Boolean),
      });
    }
    setRows(list);
    return list;
  }, []);

  const ensureSeeded = useCallback(async () => {
    const existing = await refresh();
    if (!autoSeed || !canEdit) return existing;

    const kindsPresent = new Set(existing.map((r) => r.kind));
    const missing = ONBOARDING_CONNECTION_KINDS.filter((k) => !kindsPresent.has(k));
    if (missing.length === 0) return existing;

    setSeeding(true);
    for (const kind of missing) {
      try {
        await connectionsApi.create(kind);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`seed ${kind} failed`, e?.message);
      }
    }
    setSeeding(false);
    return refresh();
  }, [autoSeed, canEdit, refresh]);

  const ensureConnection = useCallback(async (kind) => {
    const existing = await refresh();
    const row = rowByKind(existing, kind);
    if (row) return row;
    if (!canEdit) {
      throw new Error("Only workspace admins can add integrations");
    }
    setSeeding(true);
    try {
      const created = await connectionsApi.create(kind);
      await refresh();
      return created;
    } finally {
      setSeeding(false);
    }
  }, [canEdit, refresh]);

  useEffect(() => {
    if (!isAuthed) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await ensureSeeded();
        if (!cancelled) setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Couldn't load connections");
          toast.error(e?.message || "Couldn't load connections");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthed, ensureSeeded]);

  const byKind = useMemo(() => {
    const kinds = [...new Set(rows.map((r) => r.kind))];
    return Object.fromEntries(kinds.map((k) => [k, rowByKind(rows, k)]));
  }, [rows]);

  const getRow = useCallback((kind) => byKind[kind] || null, [byKind]);

  const getStatus = useCallback((kind) => {
    if (kind === "calendar" || kind === "gmail") {
      const gmail = byKind.gmail;
      if (gmail?.configured) return "connected";
      return gmail?.status || "unpaired";
    }
    return byKind[kind]?.status || "unpaired";
  }, [byKind]);

  const isConfigured = useCallback((kind) => {
    if (kind === "calendar") {
      const gmail = byKind.gmail;
      return isConfiguredStatus(
        gmail?.status, gmail?.configured, gmail?.account_identifier, gmail?.channel_config, "gmail",
      );
    }
    const row = byKind[kind];
    return isConfiguredStatus(
      row?.status, row?.configured, row?.account_identifier, row?.channel_config, kind,
    );
  }, [byKind]);

  const configuredCount = useMemo(() => {
    try {
      return ONBOARDING_CONNECTION_KINDS.filter((k) => {
        const row = byKind[k];
        if (!row) return false;
        return isConfiguredStatus(
          row.status, row.configured, row.account_identifier, row.channel_config, k,
        );
      }).length;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[connections] configuredCount failed", { byKind, error: e });
      return 0;
    }
  }, [byKind]);

  return {
    rows,
    byKind,
    loading,
    seeding,
    error,
    canEdit,
    refresh,
    ensureSeeded,
    ensureConnection,
    getRow,
    getStatus,
    isConfigured,
    configuredCount,
  };
}

export default useBackendConnections;
